/**
 * P4-04: RightToolbar —— 右栏日志区上方工具条。
 *
 * 包含：
 * - Clear log：调用 `gamePage.clearLog()`（引擎会清空 console.messages 并重置 lastDateMessage）；
 * - Filters toggle：展开/收起过滤器抽屉；
 * - Pause：显示 `game.isPaused` 状态，点击调用 `game.togglePause()`；
 * - Undo：根据 `game.undoChange` 显示/隐藏，点击调用 `game.undo()`。
 *
 * 保留旧 DOM id（`clearLogHref`、`pauseBtn`、`undoBtn`、`logFiltersBlock` 等）
 * 以兼容旧 CSS 与可能的外部脚本。
 */
import { useCallback } from "react";
import { useGameTick } from "../../bridge/useGameTick";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";

interface ToolbarSnapshot {
  isPaused: boolean;
  undoChange: { ttl: number } | null;
  ticksPerSecond: number;
  hasUnlockedFilters: boolean;
}

function getToolbarSnapshot(game: GamePage | null): ToolbarSnapshot | null {
  if (!game) {
    return null;
  }
  const filters = game.console?.filters as Record<string, { unlocked?: boolean }> | undefined;
  return {
    isPaused: !!game.isPaused,
    undoChange: game.undoChange ?? null,
    ticksPerSecond: game.ticksPerSecond ?? 5,
    hasUnlockedFilters: filters ? Object.values(filters).some((f) => f.unlocked) : false,
  };
}

function snapEq(a: ToolbarSnapshot | null, b: ToolbarSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.isPaused === b.isPaused &&
    a.ticksPerSecond === b.ticksPerSecond &&
    a.hasUnlockedFilters === b.hasUnlockedFilters &&
    (a.undoChange === null) === (b.undoChange === null) &&
    (a.undoChange === null || b.undoChange === null || a.undoChange.ttl === b.undoChange.ttl)
  );
}

interface RightToolbarProps {
  showFilters: boolean;
  onToggleFilters: () => void;
}

export default function RightToolbar({ showFilters, onToggleFilters }: RightToolbarProps) {
  const selector = useCallback(
    (game: GamePage | null) => getToolbarSnapshot(game),
    [],
  );
  const snapshot = useGameTick(selector, snapEq);

  if (!snapshot) {
    return <div className="log-toolbar" />;
  }

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    const game = (globalThis as { gamePage?: GamePage }).gamePage;
    if (game?.clearLog) {
      game.clearLog();
    }
  };

  const handlePause = (e: React.MouseEvent) => {
    e.preventDefault();
    const game = (globalThis as { gamePage?: GamePage }).gamePage;
    if (game?.togglePause) {
      game.togglePause();
    }
  };

  const handleUndo = (e: React.MouseEvent) => {
    e.preventDefault();
    const game = (globalThis as { gamePage?: GamePage }).gamePage;
    if (game?.undo) {
      game.undo();
    }
  };

  const undoVisible = snapshot.undoChange !== null;
  const undoTtl = snapshot.undoChange?.ttl ?? 0;
  const undoText = undoVisible
    ? t("ui.undo", [Math.floor(undoTtl / snapshot.ticksPerSecond)])
    : t("ui.undo");

  return (
    <div className="log-toolbar">
      <span id="clearLog">
        <a id="clearLogHref" href="#" className="link" onClick={handleClear}>
          {t("ui.clear.log")}
        </a>
      </span>
      {snapshot.hasUnlockedFilters && (
        <span id="logFiltersBlock" style={{ paddingTop: "5px" }}>
          <a
            id="logFiltersBlockText"
            className="link"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onToggleFilters();
            }}
          >
            [<span id="filterIcon">{showFilters ? "-" : "+"}</span>] {t("ui.log.filters.block")}
          </a>
        </span>
      )}
      <a
        id="pauseBtn"
        className="link"
        style={{ fontSize: "12px" }}
        href="#"
        title={t("ui.pause.title")}
        onClick={handlePause}
      >
        {snapshot.isPaused ? t("ui.unpause") : t("ui.pause")}
      </a>
      {undoVisible && (
        <a
          id="undoBtn"
          className="link"
          style={{ fontSize: "12px" }}
          href="#"
          title={t("ui.undo.title")}
          onClick={handleUndo}
        >
          {undoText}
        </a>
      )}
    </div>
  );
}
